import { useRef, useState, useEffect, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux';
import { RootState } from '../../../app/store';
import { classNames } from '../../../utils/classNames';
import { useChatboxMutation, useGetBrandingConfigQuery, useUploadChatFileMutation } from '../../../store/chatbox/chatboxApi';
import { useGetCurrentUserQuery } from '../../../store/user/userApi';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { setChatId, setChatMessages } from '../../../store/slices/chats.slice';
import { useTheme } from '../../../utils/theme-context';
import S3ExcelPreview from '../s3-preview/S3ExcelPreview';
import S3PdfPreview from '../s3-preview/S3PdfPreview';

type SpeechRecognition = any
type SpeechRecognitionEvent = any

const ChatInput = ({
	onSend
}: {
	onSend?: () => void
}) => {
	const [files, setFiles] = useState<{name: string, url: string}[]>([])
	const { theme } = useTheme();
	const dispatch = useAppDispatch();
	const [searchParams] = useSearchParams();
	const newChat = searchParams.get('newChat');
	const [isListening, setIsListening] = useState(false);
	const recognitionRef = useRef<SpeechRecognition | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const { data: userInfo } = useGetCurrentUserQuery();
	const [chatBoxAdd, { isLoading }] = useChatboxMutation()
	const [chatboxFile] = useUploadChatFileMutation();

	const {data: brand} = useGetBrandingConfigQuery(userInfo?.hotelInfo.username || '', {
		skip: !userInfo?.id
	});

	const { register, handleSubmit, reset, setFocus, setValue } = useForm<{ message: string }>();
	const { searchBar } = useSelector(
		(state: RootState) => state.features
	);
	const { topicId, showChatInput, clarifyMode } = useSelector(
		(state: RootState) => state.chats
	);

	useEffect(() => {
		const SpeechRecognition =
			(window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

		if (SpeechRecognition) {
			const recognition = new SpeechRecognition();
			recognition.lang = 'en-US';
			recognition.interimResults = false;
			recognition.maxAlternatives = 1;

			recognition.onresult = (event: SpeechRecognitionEvent) => {
				const transcript = event.results[0][0].transcript;
				if (inputRef.current) {
					inputRef.current.value = transcript;
				}
				setValue('message', transcript);
			};

			recognition.onend = () => {
				setIsListening(false);
			};

			recognition.onerror = (err: any) => {
				console.error('Speech recognition error', err);
				setIsListening(false);
			};

			recognitionRef.current = recognition;
		} else {
			console.error('Speech recognition not supported in this browser');
		}
	}, []);

	const onSubmit = async ({ message }: { message: string }) => {
		const user = { name: userInfo?.name || '', email: userInfo?.email || '', id: userInfo?.id || '' }
		try {
			reset({ message: '' })
			setFiles([]);
			onSend && onSend()
			setTimeout(() => {
				document.querySelector('#chatEnd')?.scrollIntoView({ behavior: 'smooth' });
			}, 300)
			await chatBoxAdd(
				topicId ? {
					topic: topicId, message, user, files
				} : { message, user, files }
			).unwrap();
		} catch (error) {
			const err = error as { name: string }
			if (err?.name === 'AbortError') {
				// console.log('Request was cancelled.');
			}
		}
	}

	const handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
		if (evt.key === 'Enter' && !evt.shiftKey) {
			evt.preventDefault();
			handleSubmit(onSubmit)();
		}
	};

	useEffect(() => {
		if (newChat) {
			onSend && onSend()
			dispatch(setChatMessages([]))
			dispatch(setChatId(''))
		}
		setTimeout(() => {
			setFocus('message')
		}, 100)
		return () => {
			dispatch(setChatId(''))
		};
	}, [newChat])

	const handleMicClick = () => {
		if (isListening || !recognitionRef.current) return;
		setIsListening(true);
		recognitionRef.current.start();
	};

	const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			try {
				reset({ message: '' })
				onSend && onSend()
				const formData = new FormData();
				formData.append('files', file);
				const newFile = await chatboxFile(formData).unwrap();
				setFiles(files ? [...files, ...newFile] : newFile);
				// setFileUrls();
			} catch (error) {
				const err = error as { name: string }
				if (err?.name === 'AbortError') {
					// console.log('Request was cancelled.');
				} else {
					// console.log({ err })
				}
			}
			}
	  };

	  const handleRemoveFile = (name: string) => {
		const tempFile = [...files].filter(file => file.name !== name)
		setFiles(tempFile)
	}

	return (
		(showChatInput || clarifyMode) ?
		<form className={classNames('flex flex-wrap max-w-full md:w-[760px] relative mx-auto', searchBar ? '' : 'opacity-10')} onSubmit={handleSubmit(onSubmit)}>
			{files && files.map(file => 
				<div key={file.name} className='m-2 relative'>
					<button onClick={()=>handleRemoveFile(file.name)} className='absolute -top-2 -right-2 bg-white rounded-full' type='button'>
						<img alt='close' width={16} height={16} className='' src='/svg/icons/close-modal.svg' />
				</button>
				{(file.name.includes('.pdf')) ?
				<div className='overflow-auto w-20 h-20 border border-gray-200'>
					<S3PdfPreview pdfUrl={file.url} />
				</div> :
				(file.name.includes('.xlsx') || file.name.includes('.csv')) ? <div className='overflow-auto w-20 h-20 border border-gray-200'>
					<S3ExcelPreview s3Url={file.url} />
				</div> :
				<div className='w-20 h-20 bg-gray-100 border border-gray-200'>
					<img src={file.url} className='w-full h-full object-cover' alt={file.name} />
				</div>
				}
			</div>
		)}
			{!searchBar && <div className='absolute top-0 left-0 w-full h-full opacity-10 z-10' />}
			<div className='relative w-full'>
				<button
					type="button"
					onClick={handleMicClick}
					disabled={isListening}
					className={classNames(
						'absolute left-3 top-1/2 transform -translate-y-1/2 w-7 h-7',
						isListening ? 'animate-pulse' : ''
					)}
				>
					<img src={theme === 'dark' ? '/svg/icons/microphone-white.svg' : '/svg/icons/microphone.svg'} alt='Mic' />
				</button>
				<input
					{...register('message')}
					autoComplete='off'
					onKeyDown={handleKeyDown}
					placeholder='Ask something...'
					className='dark:bg-black dark:text-primary-darkMode border-2 border-primary-default outline-none dark:border-[#C8CEDA] rounded-2xl py-3 px-4 pl-10 w-full text-sm pr-16'
					style={(process.env.REACT_APP_PROJECT !== 'nynn' && brand?.metadata.searchBarBorderColor) ? {borderColor: theme === 'dark' ? 'white' : brand?.metadata.searchBarBorderColor } : {}}
					type="text"
				/>
				<label
					htmlFor='file-upload'
					className='absolute right-10 top-2.5 cursor-pointer'
				>
					<img src={theme === 'dark' ? '/svg/icons/file-upload-white.svg' : '/svg/icons/file-upload.svg'} className='object-contain w-[25px]' />
					<input id="file-upload" type="file" className='hidden' onChange={handleFileUpload} />
				</label>
				<button className='absolute right-3 top-2.5' disabled={isLoading} type='submit'>
					<img src={theme === 'dark' ? '/svg/icons/send-white.svg' : '/svg/icons/send.svg'} className='object-contain w-[25px]' />
				</button>
			</div>
		</form>: null
	)
}

export default ChatInput